import React, { useEffect, useMemo, useState } from "react"
import { observe } from "mobx"
import { observer } from "mobx-react"
import { Checkbox, Col, Collapse, Flex, Form, Row, Typography } from "antd"
import classNames from "classnames"

import css from "./index.module.css"
type Props = {
  isLoading?: boolean
  isOpen?: boolean
  isReadonly?: boolean
  scopesGroups: Record<string, string[]>
  selected?: string[]
  updateOpen?: (v: boolean) => void
}
const collapseKey = "1"

const ScopesSelect = observer(
  ({ isOpen, isReadonly, scopesGroups, selected, updateOpen }: Props) => {
    const form = Form.useFormInstance()

    const checkedValues = (
      (Form.useWatch("scopes", form) ?? []) as string[]
    ).filter((v) => v !== collapseKey)

    const [activeKeys, setActiveKeys] = useState<string[]>(
      isReadonly ? [collapseKey] : [],
    )

    useEffect(() => {
      setActiveKeys(isOpen || isReadonly ? [collapseKey] : [])
    }, [isOpen, isReadonly])

    useEffect(() => {
      const selectAll = !selected || selected.includes("*")

      if (selectAll) {
        form.setFieldsValue({ scopes: Object.values(scopesGroups).flat() })
      } else {
        form.setFieldsValue({ scopes: selected })
      }
    }, [scopesGroups, selected, isOpen])

    const handleParentChange = (groupKey: string, groupValues: string[]) => {
      const children = scopesGroups[groupKey]

      if (children) {
        const isAllSelected = children.every((v) => checkedValues.includes(v))

        const newChecked = isAllSelected
          ? checkedValues.filter((v) => !children.includes(v))
          : [
              ...checkedValues,
              ...children.filter((v) => !checkedValues.includes(v)),
            ]

        form.setFieldsValue({ scopes: newChecked })
      }
    }

    const handleChildrenChange = (
      groupKey: string,
      selectedInGroup: string[],
    ) => {
      const otherGroupsValues = Object.entries(scopesGroups)
        .filter(([key]) => key !== groupKey)
        .flatMap(([, values]) => values)

      const newChecked = [
        ...selectedInGroup,
        ...checkedValues.filter((v) => otherGroupsValues.includes(v)),
      ]

      form.setFieldsValue({ scopes: newChecked })
    }

    const collapseChange = (e: string[]) => {
      if (updateOpen) {
        updateOpen(Boolean(e.length))
      } else {
        setActiveKeys(e)
      }
    }

    // const allIsChecked = useMemo(() => {
    //   return Object.values(scopesGroups).flat().length === checkedValues.length
    // }, [scopesGroups, checkedValues])
    //
    // const checkAll = () => {
    //   if (allIsChecked) {
    //     form.setFieldsValue({ scopes: [] })
    //   } else {
    //     form.setFieldsValue({
    //       scopes: Object.values(scopesGroups).flat(),
    //     })
    //   }
    // }

    return (
      <Form.Item
        colon={false}
        id="GENERATED_ID_SETTINGSAPIKEYS_FORM_ITEM_VOID_1"
        label={" "}
        name="scopes"
        required={false}
        rules={[
          {
            required: true,
            validator: (_, value) => {
              if (value && value.length === 0) {
                return Promise.reject(
                  new Error("Please select at least one scope"),
                )
              }

              return Promise.resolve()
            },
          },
        ]}
      >
        <Collapse
          accordion
          activeKey={activeKeys}
          bordered={false}
          onChange={(e) => collapseChange(e as string[])}
        >
          <Collapse.Panel
            disabled={isReadonly}
            extra={"Scopes define the access for tokens"}
            header={
              <Flex gap={16} style={{ width: 150 }}>
                <Typography.Text>Scopes</Typography.Text>
              </Flex>
            }
            key="1"
          >
            <Row className={classNames(css.scopes)} style={{ width: "100%" }}>
              {Object.entries(scopesGroups).map(([key, val]) => {
                const someCheck = checkedValues.some((v) => val.includes(v))
                const render = isReadonly ? someCheck : true
                return (
                  render && (
                    <Col className={css.scopesCol} key={key} span={8}>
                      <Flex className={css.scopesChild} gap={8} vertical>
                        <Checkbox
                          checked={val.every((v) => checkedValues.includes(v))}
                          disabled={isReadonly}
                          indeterminate={
                            someCheck &&
                            !val.every((v) => checkedValues.includes(v))
                          }
                          key={key}
                          onChange={() => handleParentChange(key, val)}
                        >
                          {key}
                        </Checkbox>

                        <Checkbox.Group
                          disabled={isReadonly}
                          name="scopes"
                          onChange={(vals) => handleChildrenChange(key, vals)}
                          options={val.map((v) => ({ label: v, value: v }))}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                            paddingLeft: 16,
                          }}
                          value={checkedValues.filter((v) => val.includes(v))}
                        />
                      </Flex>
                    </Col>
                  )
                )
              })}
            </Row>
          </Collapse.Panel>
        </Collapse>
      </Form.Item>
    )
  },
)

export default ScopesSelect
